
import { PDFDocument, rgb } from 'pdf-lib';

import fontkit from '@pdf-lib/fontkit';

import { getDate, capitalizeEveryWord, superscript, blobToArrayBuffer, fetchUint8Array, drawText, drawRectangle, savePDF } from './utils.js';

import boldItalicFontPath from '../fonts/benton-sans-bold-italic.otf';

import boldFontPath from '../fonts/benton-sans-bold.otf';

import condensedBoldFontPath from '../fonts/benton-sans-condensed-bold.otf';

import condensedItalicFontPath from '../fonts/benton-sans-condensed-italic.otf';

import mediumFontPath from '../fonts/benton-sans-medium.otf';

import regularFontPath from '../fonts/benton-sans-regular.otf';

import templatePath from './report.pdf';

import zipCode from '../state/zip-code.js';

import place from '../state/place.js';

import nationalAverage from '../state/national-average.js';

import demographicStats from '../state/demographic-stats.js';

import metricStats from '../state/metric-stats.js';

import healthStats from '../state/health-stats.js';

function drawCover( doc, page, regularFont ) {

  drawText( { doc, page, font: regularFont, size: 18, x: 88, y: 390, align: 'left', color: 0x26447c, text: `${ capitalizeEveryWord( place.county ) }, ${ place.stateAbbr.toUpperCase() } (${ zipCode.value })` } );

  drawText( { doc, page, font: regularFont, size: 18, x: 138, y: 411.6, align: 'left', color: 0x26447c, text: getDate() } );

}

function drawHeader( doc, page, regularFont ) {

  drawText( {
    
    doc, page, font: regularFont, size: 12.5, x: page.getWidth() / 2, y: 57, align: 'center', color: 0xffffff,
    
    text: `${ capitalizeEveryWord( place.county ) }, ${ place.stateAbbr.toUpperCase() } (${ zipCode.value }) |  Generated: ${ getDate() }`
  
  } );

}

function drawDems( doc, page, boldFont ) {

  const positions = [ { x: 378, y: 167.6 }, { x: 383, y: 213.5 }, { x: 440, y: 259.15 }, { x: 465, y: 305.7 }, { x: 365, y: 352 } ];

  demographicStats.value.forEach( ( stat, index ) =>

    drawText( { ...positions[ index ], doc, page, font: boldFont, size: 12.5, align: 'left', color: 0x26447c, text: stat.value } ) );

}

function drawCardBox( options ) {

  const {
    
    doc, page, x, y, width, height, barHeight, paddingTop, boxColor, noData,
    
    headingFont, heading, headingColor,
    
    localFont, local, localColor,
    
    descriptionFont, description, descriptionColor,
    
    nationalFont, national, nationalColor
  
  } = {
    
    width: 168, height: 138, barHeight: 28, paddingTop: 0,

    ...options
  
  };

  drawRectangle( { page, x, y, width, height, color: boxColor } );

  drawRectangle( { page, x: x + 1, y: y + barHeight, width: width - 2, height: height - barHeight - 1, color: 0xffffff } );

  drawText( { doc, page, x: x + width / 2, y: y + barHeight / 2 - 3, font: headingFont, size: 10, align: 'center', color: headingColor, text: heading } );

  if ( noData ) {
   
    let { y: lastY, height: lastHeight } = drawText( { doc, page, x: x + width / 2, y: y + height / 2 + paddingTop + 8, font: localFont, size: 34, align: 'center', color: localColor, text: 'N/A' } );

    drawText( { doc, page, x: x + width / 2, y: lastY + lastHeight, font: nationalFont, size: 9, align: 'center', color: nationalColor, text: 'Data Not Available' } );

  }

  else {

    drawText( { doc, page, x: x + width / 2, y: y + height / 2 + paddingTop - 11, font: localFont, size: 34, align: 'center', color: localColor, text: local } );

    let { y: lastY, height: lastHeight } = drawText( { doc, page, x: x + width / 2 + 5, y: y + height / 2 + paddingTop + 15 , maxWidth: 100, align: 'top center', font: descriptionFont, size: 10, color: descriptionColor, text: description } );

    drawText( { doc, page, x: x + width / 2, y: lastY + lastHeight + 10, font: nationalFont, size: 9, align: 'center', color: nationalColor, text: national } );

  }

}

function drawPopMetrics( doc, page, boldFont, condensedBoldFont, condensedItalicFont, mediumFont ) {

  const positions = [ { x: 36, y: 441 }, { x: 222, y: 441 }, { x: 408, y: 441 }, { x: 36, y: 597 }, { x: 222, y: 597 }, { x: 408, y: 597 } ];

  metricStats.value.forEach( ( stat, index ) => drawCardBox( {
    
    doc, page, ...positions[ index ], boxColor: stat.alert ? 0xe22828 : 0x00427f, noData: ! stat.localValue || isNaN( stat.localValue ),
    
    headingFont: boldFont, heading: capitalizeEveryWord( stat.statistic ), headingColor: 0xffffff,
    
    localFont: condensedBoldFont, local: stat.localValue ? `${ stat.localValue }%` : 'N/A', localColor: stat.alert ? 0xe22828 : 0x373737,
    
    descriptionFont: mediumFont, description: 'of county residents', descriptionColor: 0x373737,
     
    nationalFont: condensedItalicFont, national: `${ stat.alert ? 'Above ' : '' }${ stat.nationalValue }% National Average`, nationalColor: 0xaaaaaa
  
  } ) );

}

function drawRPMetrics( doc, page, boldFont, condensedBoldFont, condensedItalicFont, mediumFont ) {

  const positions = [ { x: 36, y: 147 }, { x: 222, y: 147 }, { x: 408, y: 147 } ];

  healthStats.value.filter( stat => stat.tab === 'respiratory' ).forEach( ( stat, index ) => drawCardBox( {
    
    doc, page, ...positions[ index ], height: 153, paddingTop: -5, boxColor: stat.alert ? 0xe22828 : 0x4dc3e0, noData: ! stat.localValue || isNaN( stat.localValue ),
    
    headingFont: boldFont, heading: `${ stat.title.toUpperCase() }${ superscript( stat.reference ) }`, headingColor: stat.alert ? 0xffffff : 0x373737,
    
    localFont: condensedBoldFont, local: `${ stat.localValue }${ stat.suffix ?? '' }`, localColor: stat.alert ? 0xe22828 : 0x373737,
    
    descriptionFont: mediumFont, description: stat.description, descriptionColor: 0x373737,
     
    nationalFont: condensedItalicFont, national: `${ stat.alert ? 'Above ' : '' }${ stat.nationalValue }${ stat.suffix ?? '' } National Average`, nationalColor: 0xaaaaaa
  
  } ) );

}

function drawGIMetrics( doc, page, boldItalicFont, boldFont, condensedBoldFont, condensedItalicFont, mediumFont ) {

  const positions = [ { x: 36, y: 376 }, { x: 222, y: 376 }, { x: 408, y: 376 }, { x: 36, y: 546 }, { x: 222, y: 546 } ];

  healthStats.value.filter( stat => stat.tab === 'gastrointestinal' ).forEach( ( stat, index ) => drawCardBox( {
    
    doc, page, ...positions[ index ], height: 153, paddingTop: -5, boxColor: stat.alert ? 0xe22828 : 0xc0d630, noData: ! stat.localValue || isNaN( stat.localValue ),
    
    headingFont: stat.title.match( /<i>/ ) ? boldItalicFont : boldFont, headingColor: stat.alert ? 0xffffff : 0x373737,

    heading: `${ stat.title.replace( /<\/?i>/g, '' ).toUpperCase() }${ superscript( stat.reference ) }`,
    
    localFont: condensedBoldFont, local: stat.localValue, localColor: stat.alert ? 0xe22828 : 0x373737,
    
    descriptionFont: mediumFont, description: stat.description, descriptionColor: 0x373737,
     
    nationalFont: condensedItalicFont, national: `${ stat.alert ? 'Above ' : '' }${ stat.nationalValue } National Average`, nationalColor: 0xaaaaaa
  
  } ) );

}

function drawMEMetrics( doc, page, boldItalicFont, boldFont, condensedBoldFont, condensedItalicFont, mediumFont ) {

  const positions = [ { x: 36, y: 147 }, { x: 222, y: 147 }, { x: 408, y: 147 } ];

  healthStats.value.filter( stat => stat.tab === 'meningitis' ).forEach( ( stat, index ) => drawCardBox( {
    
    doc, page, ...positions[ index ], height: 153, paddingTop: -5, boxColor: stat.alert ? 0xe22828 : 0xf4b334, noData: ! stat.localValue || isNaN( stat.localValue ),
    
    headingFont: stat.title.match( /<i>/ ) ? boldItalicFont : boldFont, headingColor: stat.alert ? 0xffffff : 0x373737,

    heading: `${ stat.title.replace( /<\/?i>/g, '' ).toUpperCase() }${ superscript( stat.reference ) }`,
    
    localFont: condensedBoldFont, local: stat.localValue, localColor: stat.alert ? 0xe22828 : 0x373737,
    
    descriptionFont: mediumFont, description: stat.description, descriptionColor: 0x373737,
     
    nationalFont: condensedItalicFont, national: `${ stat.alert ? 'Above ' : '' }${ stat.nationalValue } National Average`, nationalColor: 0xaaaaaa
  
  } ) );

}

function drawTFMetrics( doc, page, boldFont, condensedBoldFont, condensedItalicFont, mediumFont ) {

  const positions = [ { x: 36, y: 376 }, { x: 222, y: 376 } ];

  healthStats.value.filter( stat => stat.tab === 'tropical-fever' ).forEach( ( stat, index ) => drawCardBox( {
    
    doc, page, ...positions[ index ], height: 153, paddingTop: -5, boxColor: stat.alert ? 0xe22828 : 0xe65f1c, noData: ! stat.localValue || isNaN( stat.localValue ),
    
    headingFont: boldFont, headingColor: stat.alert ? 0xffffff : 0x373737,

    heading: `${ stat.title.toUpperCase() }${ superscript( stat.reference ) }`,
    
    localFont: condensedBoldFont, local: stat.localValue, localColor: stat.alert ? 0xe22828 : 0x373737,
    
    descriptionFont: mediumFont, description: stat.description, descriptionColor: 0x373737,
     
    nationalFont: condensedItalicFont, national: `${ stat.alert ? 'Above ' : '' }${ stat.nationalValue } National Average`, nationalColor: 0xaaaaaa
  
  } ) );

}

function drawBSIMetrics( doc, page, boldItalicFont, boldFont, condensedBoldFont, condensedItalicFont, mediumFont ) {

  const positions = [ { x: 36, y: 605 }, { x: 222, y: 605 }, { x: 408, y: 605 } ];

  healthStats.value.filter( stat => stat.tab === 'bloodstream-infections' ).forEach( ( stat, index ) => drawCardBox( {
    
    doc, page, ...positions[ index ], height: 153, paddingTop: -5, boxColor: stat.alert ? 0xe22828 : 0x0, noData: ! stat.localValue || isNaN( stat.localValue ),
    
    headingFont: stat.title.match( /<i>/ ) ? boldItalicFont : boldFont, headingColor: 0xffffff,

    heading: `${ stat.title.replace( /<\/?i>/g, '' ).toUpperCase() }${ superscript( stat.reference ) }`,
    
    localFont: condensedBoldFont, local: stat.localValue, localColor: stat.alert ? 0xe22828 : 0x373737,
    
    descriptionFont: mediumFont, description: stat.description, descriptionColor: 0x373737,
     
    nationalFont: condensedItalicFont, national: `${ stat.alert ? 'Above ' : '' }${ stat.nationalValue } National Average`, nationalColor: 0xaaaaaa
  
  } ) );

}

export async function exportPDF() {

  const boldItalicFontBytes = await fetchUint8Array( boldItalicFontPath );

  const boldFontBytes = await fetchUint8Array( boldFontPath );

  const condensedBoldFontBytes = await fetchUint8Array( condensedBoldFontPath );

  const condensedItalicFontBytes = await fetchUint8Array( condensedItalicFontPath );

  const mediumFontBytes = await fetchUint8Array( mediumFontPath );

  const regularFontBytes = await fetchUint8Array( regularFontPath );
  
  const templateBytes = await fetchUint8Array( templatePath );
  
  const doc = await PDFDocument.load( templateBytes );
  
  doc.registerFontkit( fontkit );
  
  const boldItalicFont = await doc.embedFont( boldItalicFontBytes );

  const boldFont = await doc.embedFont( boldFontBytes );

  const condensedBoldFont = await doc.embedFont( condensedBoldFontBytes );

  const condensedItalicFont = await doc.embedFont( condensedItalicFontBytes );

  const mediumFont = await doc.embedFont( mediumFontBytes );

  const regularFont = await doc.embedFont( regularFontBytes );
  
  const pages = doc.getPages();

  drawCover( doc, pages[ 0 ], regularFont );

  for ( let i = 1; i < 4; i ++ ) drawHeader( doc, pages[ i ], regularFont );
  
  drawDems( doc, pages[ 1 ], boldFont );

  drawPopMetrics( doc, pages[ 1 ], boldFont, condensedBoldFont, condensedItalicFont, mediumFont );

  drawRPMetrics( doc, pages[ 2 ], boldFont, condensedBoldFont, condensedItalicFont, mediumFont );

  drawGIMetrics( doc, pages[ 2 ], boldItalicFont, boldFont, condensedBoldFont, condensedItalicFont, mediumFont );

  drawMEMetrics( doc, pages[ 3 ], boldItalicFont, boldFont, condensedBoldFont, condensedItalicFont, mediumFont );

  drawTFMetrics( doc, pages[ 3 ], boldFont, condensedBoldFont, condensedItalicFont, mediumFont );

  drawBSIMetrics( doc, pages[ 3 ], boldItalicFont, boldFont, condensedBoldFont, condensedItalicFont, mediumFont );

  savePDF( `Population Health Data for ${ zipCode.value }`, doc );
  
}
